import { Component, Output, EventEmitter, Input, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LosStateService } from '../los-state.service';
import { LoanApplicationService } from '../../loan-applications/loan-applications.service';
import { UserLoginService } from 'src/app/login/userlogin.service';
import { LoanApplicationUpdateDTO, LoanApplicationItem } from '../../loan-applications/loan-applications.interface';
import { ProductDTO } from '../../Interface & Service/product/product.interface';
import { UserKYCDTO } from '../../kyc/userkyc.interface';
import { ApplicationDetailsDTO } from '../../loan-applications/loan-application-details/loan-application-details.interface';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-kfs-delivery',
  templateUrl: './kfs-delivery.component.html',
  styleUrls: ['./kfs-delivery.component.css']
})
export class KfsDeliveryComponent implements OnInit {
  @Input() applicationId: number | null = null;
  @Output() next = new EventEmitter<void>();
  @Output() prev = new EventEmitter<void>();

  private losState = inject(LosStateService);
  private loanApplicationService = inject(LoanApplicationService);
  private userLoginService = inject(UserLoginService);
  private toastr = inject(ToastrService);
  private destroyRef = inject(DestroyRef);

  applicationData: LoanApplicationItem | null = null;
  productData: ProductDTO | null = null;
  userKycData: UserKYCDTO | null = null;
  applicationDetails: ApplicationDetailsDTO[] = [];

  bankAccount = '';
  bankIfsc = '';
  ackReceived = false;
  ackTime = '';
  isLoading = false;

  ngOnInit(): void {
    // 1. Subscribe to application data
    this.losState.applicationData$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(app => this.applicationData = app);

    // 2. Subscribe to product data
    this.losState.productData$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(prod => this.productData = prod);

    // 3. Subscribe to KYC data
    this.losState.userKycData$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(kyc => this.userKycData = kyc);

    // 4. Subscribe to application details
    this.losState.applicationDetails$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(details => {
        if (details) {
          this.applicationDetails = details;
          const acc = details.find(d => d.parameterName === 'Bank Account Number')?.value;
          const ifsc = details.find(d => d.parameterName === 'Bank IFSC')?.value;
          if (acc) this.bankAccount = acc;
          if (ifsc) this.bankIfsc = ifsc;
        }
      });
  }

  private get actionUser(): string {
    return this.userLoginService.User()?.userId?.toString() || '0';
  }

  // Getters for KFS calculations
  get loanAmount(): number {
    return this.applicationData?.requestedAmount || 75000;
  }

  get interestRatePct(): number {
    return this.productData?.interestRate || 14.5;
  }

  get tenureMonths(): number {
    return this.applicationData?.tenureMonths || 12;
  }

  get processingFeePercent(): number {
    return this.productData?.processingFeePercent || 2;
  }

  get processingFeeRaw(): number {
    return Math.round(this.loanAmount * (this.processingFeePercent / 100));
  }

  get processingFeeGst(): number {
    return Math.round(this.processingFeeRaw * 0.18);
  }

  get processingFeeTotal(): number {
    return this.processingFeeRaw + this.processingFeeGst;
  }

  get emi(): number {
    const P = this.loanAmount;
    const r = (this.interestRatePct / 12) / 100;
    const n = this.tenureMonths;
    if (r > 0) {
      return Math.round((P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1));
    }
    return Math.round(P / n);
  }

  get totalInterest(): number {
    return Math.round((this.emi * this.tenureMonths) - this.loanAmount);
  }

  get totalCostOfCredit(): number {
    return this.loanAmount + this.totalInterest + this.processingFeeTotal;
  }

  get apr(): number {
    const loanAmount = this.loanAmount;
    const processingFeeTotal = this.processingFeeTotal;
    const emi = this.emi;
    const tenure = this.tenureMonths;
    const netDisbursed = loanAmount - processingFeeTotal;

    if (netDisbursed <= 0 || emi <= 0) return this.interestRatePct;

    // Solver for IRR
    let low = 0.0001;
    let high = 0.20;
    for (let i = 0; i < 40; i++) {
      const mid = (low + high) / 2;
      const val = emi * (1 - Math.pow(1 + mid, -tenure)) / mid;
      if (val > netDisbursed) {
        low = mid;
      } else {
        high = mid;
      }
    }
    const r = (low + high) / 2;
    return r * 12 * 100;
  }

  get maskedBankAccount(): string {
    if (!this.bankAccount) return 'SBI XXXX4892';
    if (this.bankAccount.length <= 4) return this.bankAccount;
    return 'XXXX' + this.bankAccount.substring(this.bankAccount.length - 4);
  }

  get borrowerFirstName(): string {
    const fullName = this.userKycData?.name?.trim();
    if (!fullName) return 'Borrower';
    return fullName.split(/\s+/)[0];
  }

  sendKfs(): void {
    this.toastr.success(`KFS Statement sent successfully via SMS & email to ${this.userKycData?.mobile || 'borrower'}`);
  }

  simulateAck(): void {
    this.ackReceived = true;
    this.ackTime = new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
    this.toastr.info('Acknowledgement simulation active');
  }

  async proceed(): Promise<void> {
    if (this.isLoading) return;
    this.isLoading = true;

    try {
      if (this.applicationData && this.applicationId) {
        const currentStatus = this.applicationData.applicationStatus || 'Draft';
        const statusPriority: Record<string, number> = {
          'Draft': 0,
          'ConsentPending': 1,
          'KYCPending': 2,
          'KYCVerified': 3,
          'FinancialComplete': 4,
          'BureauComplete': 5,
          'CreditEvaluated': 6,
          'Approved': 6,
          'KFSDelivered': 7
        };

        const currentPrio = statusPriority[currentStatus] ?? 99;
        if (currentPrio <= 6) {
          const updatePayload: LoanApplicationUpdateDTO = {
            applicationId: this.applicationData.applicationId,
            userId: this.applicationData.userId,
            productId: this.applicationData.productId,
            applicationStatus: 'KFSDelivered',
            requestedAmount: this.applicationData.requestedAmount,
            approvedAmount: this.applicationData.approvedAmount,
            tenureMonths: this.applicationData.tenureMonths,
            applicationDate: this.applicationData.applicationDate,
            approvalDate: this.applicationData.approvalDate,
            approvedBy: this.applicationData.approvedBy || '',
            rejectionReason: this.applicationData.rejectionReason || '',
            rejectedBy: this.applicationData.rejectedBy || '',
            isDisbursed: this.applicationData.isDisbursed,
            disbursedOn: this.applicationData.disbursedOn,
            disbursedAmount: this.applicationData.disbursedAmount,
            subUserId: this.applicationData.subUserId,
            businessId: this.applicationData.businessId,
            productCode: this.applicationData.productCode || '',
            prrn: this.applicationData.prrn,
            lmsApplicationName: this.applicationData.lmsApplicationName,
            otherDetail1: this.applicationData.otherDetail1,
            otherDetail2: this.applicationData.otherDetail2,
            otherDetail3: this.applicationData.otherDetail3,
            otherDetail4: this.applicationData.otherDetail4,
            otherDetail5: this.applicationData.otherDetail5,
            otherDetail6: this.applicationData.otherDetail6,
            isActive: 1,
            actionUser: this.actionUser
          };

          const updated = await firstValueFrom(this.loanApplicationService.LoanApplication_Update(updatePayload));
          this.losState.patchApplication(updated);
        }
      }
      this.toastr.success('KFS acknowledgement recorded');
      this.isLoading = false;
      this.next.emit();
    } catch (err) {
      this.toastr.error('Failed to save KFS progress');
      console.error('KFS progress error:', err);
      this.isLoading = false;
    }
  }
}

